import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/brand/Logo';
import type { CertificateDetail } from '@/features/certificates/api';
import { verifyUrl } from '@/pages/clinical/consult-utils';
import { identityLabel, issuedLabel, periodLabel } from './mc-format';
import styles from './mc.module.css';

/** QR carrying the public check link (/verify/<code>), never the IC. */
export function McQr({ code, size = 136 }: { code: string; size?: number }) {
  const { t } = useTranslation();
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toDataURL(verifyUrl(code), { margin: 0, width: size * 2 })
      .then((d) => live && setSrc(d))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [code, size]);
  return (
    <div className={styles.qr}>
      <div className={styles.qrBox} style={{ width: size, height: size }}>
        {src && <img src={src} alt={t('certificates.qrAlt')} />}
      </div>
      <span>{t('certificates.scanToVerify')}</span>
    </div>
  );
}

/**
 * The certificate as issued: issuing branch from Clinic setup, the doctor and
 * MMC number recorded at issue, patient identity, MC dates and reference.
 * Only stored facts; no diagnosis and no added legal wording.
 */
export function McDocument({ d }: { d: CertificateDetail }) {
  const { t } = useTranslation();
  const id = identityLabel(d.patient.identity);
  return (
    <div className={styles.doc}>
      <div className={styles.head}>
        <Logo className={styles.logo} aria-hidden="true" />
        <div>
          <b>{d.branch.name}</b>
          {d.branch.address ? (
            <span>{d.branch.address.join(', ')}</span>
          ) : (
            <span className={styles.missing}>
              {d.branch.source === 'issue_snapshot'
                ? t('certificates.addressMissingAtIssue')
                : t('certificates.addressMissing')}
            </span>
          )}
          {(d.branch.phone || d.branch.mohRegistrationNo) && (
            <span>
              {[
                d.branch.phone,
                d.branch.mohRegistrationNo &&
                  t('certificates.mohNo', { no: d.branch.mohRegistrationNo }),
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
        </div>
      </div>
      <div className={styles.title}>
        <b>SIJIL CUTI SAKIT</b>
        <span>{t('certificates.docTitle')}</span>
        <span className={styles.mono}>{d.number}</span>
      </div>
      <dl className={styles.fields}>
        <div>
          <dt>{t('certificates.patientName')}</dt>
          <dd>{d.patient.fullName}</dd>
        </div>
        <div>
          <dt>{t('certificates.identity')}</dt>
          <dd>{id ?? t('certificates.noIdentity')}</dd>
        </div>
        <div>
          <dt>{t('certificates.period')}</dt>
          <dd>{periodLabel(d)}</dd>
        </div>
        <div>
          <dt>{t('certificates.duration')}</dt>
          <dd>
            {d.days === null ? '—' : t('certificates.days', { count: d.days })}
          </dd>
        </div>
      </dl>
      <div className={styles.sign}>
        <div>
          <span>{t('books.doctor')}</span>
          <b>{d.doctor.name}</b>
          <small>
            {d.doctor.mmcNumber
              ? t('certificates.mmc', { no: d.doctor.mmcNumber })
              : t('certificates.noMmc')}
          </small>
        </div>
        <div>
          <span>{t('certificates.issued')}</span>
          <b>{issuedLabel(d.issuedAt)}</b>
          <small>{t('certificates.noDiagnosis')}</small>
        </div>
      </div>
    </div>
  );
}

/**
 * A4 print sheet in its own root: while printing, everything else on the
 * page is hidden (mc.module.css), like the prototype's #printroot.
 */
export function McPrintSheet({
  d,
  onDone,
}: {
  d: CertificateDetail;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [qrReady, setQrReady] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toDataURL(verifyUrl(d.verificationCode), { margin: 0, width: 320 })
      .then((src) => {
        if (!live) return;
        setQr(src);
        setQrReady(true);
      })
      .catch(() => live && setQrReady(true));
    return () => {
      live = false;
    };
  }, [d.verificationCode]);
  useEffect(() => {
    if (!qrReady) return;
    const done = () => onDone();
    window.addEventListener('afterprint', done, { once: true });
    window.print();
    return () => window.removeEventListener('afterprint', done);
  }, [qrReady, onDone]);
  return createPortal(
    <div className={styles.printRoot} data-testid="mc-print-sheet">
      <div className={styles.sheet}>
        <McDocument d={d} />
        <div className={styles.sheetQr}>
          {qr && <img src={qr} alt={t('certificates.qrAlt')} />}
          <div>
            <b>{t('certificates.scanToVerify')}</b>
            <span className={styles.mono}>{verifyUrl(d.verificationCode)}</span>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
